import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Package, Plus, Trash2, Edit2, Check, AlertTriangle, History, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInventory = () => {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [history, setHistory] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ truck_id: 'TRUCK-001', item_name: '', quantity: '', unit: 'pcs', min_quantity: '0', barcode: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/admin/inventory`, { withCredentials: true }),
        axios.get(`${API}/inventory/requests`, { withCredentials: true }),
        axios.get(`${API}/inventory/transfers`, { withCredentials: true }),
      ]);
      setItems(a.data);
      setRequests(b.data);
      setTransfers(c.data);
    } catch (err) { console.error(err); }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/history`, { withCredentials: true });
      setHistory(data);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/inventory`, {
        ...form,
        quantity: parseInt(form.quantity),
        min_quantity: parseInt(form.min_quantity || 0),
      }, { withCredentials: true });
      toast.success('Item added');
      setForm({ truck_id: 'TRUCK-001', item_name: '', quantity: '', unit: 'pcs', min_quantity: '0', barcode: '' });
      setShowForm(false);
      refresh();
    } catch (err) { toast.error('Failed'); }
    setLoading(false);
  };

  const handleSaveEdit = async (id) => {
    try {
      const payload = { ...editForm };
      if (payload.quantity !== undefined) payload.quantity = parseInt(payload.quantity);
      if (payload.min_quantity !== undefined) payload.min_quantity = parseInt(payload.min_quantity);
      await axios.put(`${API}/admin/inventory/${id}`, payload, { withCredentials: true });
      toast.success('Updated');
      setEditingId(null);
      refresh();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/inventory/${id}`, { withCredentials: true });
      toast.success('Deleted');
      refresh();
    } catch { toast.error('Failed'); }
  };

  const handleRequestAction = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inventory/requests/${id}`, { status }, { withCredentials: true });
      toast.success(`Request ${status}`);
      refresh();
    } catch { toast.error('Failed'); }
  };

  const handleTransferAction = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inventory/transfers/${id}`, { status }, { withCredentials: true });
      toast.success(`Transfer ${status}`);
      refresh();
    } catch { toast.error('Failed'); }
  };

  const lowStockItems = items.filter(i => (i.min_quantity || 0) > 0 && (i.quantity || 0) <= (i.min_quantity || 0));

  return (
    <div data-testid="admin-inventory-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Inventory</h2>
          <p className="text-[#A3A3A3]">Truck stock, transfers & low-stock alerts</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="admin-history-btn" onClick={fetchHistory} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><History className="w-4 h-4 mr-2" /> History</Button>
          <Button data-testid="admin-add-inventory-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> Add Item</Button>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div data-testid="low-stock-banner" className="bg-[#1a0e0e] border border-red-700/40 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-red-400 font-bold uppercase tracking-wider text-xs">{lowStockItems.length} Low-Stock Alert(s)</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.map(i => (
              <span key={i.id} className="bg-[#262626] text-white text-xs px-3 py-1 rounded-full">
                {i.truck_id} • {i.item_name}: <span className="text-red-400 font-bold">{i.quantity}</span>/{i.min_quantity} {i.unit}
              </span>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-white">Truck</Label>
              <Select value={form.truck_id} onValueChange={(v) => setForm({...form, truck_id: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {['TRUCK-001','TRUCK-002','TRUCK-003','SHOP'].map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Item Name</Label>
              <Input data-testid="inv-name" value={form.item_name} onChange={(e) => setForm({...form, item_name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Barcode</Label>
              <Input data-testid="inv-barcode" value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Quantity</Label>
              <Input data-testid="inv-qty" type="number" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Min Quantity (Alert Below)</Label>
              <Input data-testid="inv-minqty" type="number" value={form.min_quantity} onChange={(e) => setForm({...form, min_quantity: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Unit</Label>
              <div className="flex gap-2">
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {['pcs','ft','rolls','boxes'].map(u => <SelectItem key={u} value={u} className="text-white">{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="inv-submit" type="submit" disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Add</Button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">Current Stock</h3>
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A0A0A] border-b border-[#262626]">
              <tr>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Truck</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Item</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Barcode</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Qty</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Min</th>
                <th className="text-left px-4 py-3 text-[#A3A3A3] text-sm">Unit</th>
                <th className="text-right px-4 py-3 text-[#A3A3A3] text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-center px-6 py-8 text-[#A3A3A3]">No items</td></tr>
              ) : items.map((i) => {
                const isLow = (i.min_quantity || 0) > 0 && (i.quantity || 0) <= (i.min_quantity || 0);
                return (
                <tr key={i.id} className={`border-b border-[#262626] last:border-0 ${isLow ? 'bg-red-900/10' : ''}`}>
                  {editingId === i.id ? (
                    <>
                      <td className="px-4 py-2"><Input value={editForm.truck_id ?? i.truck_id} onChange={(e) => setEditForm({...editForm, truck_id: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></td>
                      <td className="px-4 py-2"><Input value={editForm.item_name ?? i.item_name} onChange={(e) => setEditForm({...editForm, item_name: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></td>
                      <td className="px-4 py-2"><Input value={editForm.barcode ?? (i.barcode || '')} onChange={(e) => setEditForm({...editForm, barcode: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white font-mono" /></td>
                      <td className="px-4 py-2"><Input type="number" value={editForm.quantity ?? i.quantity} onChange={(e) => setEditForm({...editForm, quantity: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white w-20" /></td>
                      <td className="px-4 py-2"><Input type="number" value={editForm.min_quantity ?? (i.min_quantity || 0)} onChange={(e) => setEditForm({...editForm, min_quantity: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white w-20" /></td>
                      <td className="px-4 py-2"><Input value={editForm.unit ?? i.unit} onChange={(e) => setEditForm({...editForm, unit: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white w-20" /></td>
                      <td className="px-4 py-2 text-right">
                        <Button onClick={() => handleSaveEdit(i.id)} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] mr-2"><Check className="w-4 h-4" /></Button>
                        <Button onClick={() => { setEditingId(null); setEditForm({}); }} size="sm" variant="outline" className="border-[#262626] text-white">X</Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-white">{i.truck_id}</td>
                      <td className="px-4 py-3 text-white">{i.item_name}</td>
                      <td className="px-4 py-3 text-[#A3A3A3] font-mono text-xs">{i.barcode || '—'}</td>
                      <td className={`px-4 py-3 font-bold ${isLow ? 'text-red-400' : 'text-[#D4AF37]'}`}>{i.quantity}{isLow && ' ⚠'}</td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{i.min_quantity || 0}</td>
                      <td className="px-4 py-3 text-[#A3A3A3]">{i.unit}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button data-testid={`inv-edit-${i.id}`} onClick={() => { setEditingId(i.id); setEditForm({}); }} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] mr-2"><Edit2 className="w-4 h-4" /></Button>
                        <Button data-testid={`inv-delete-${i.id}`} onClick={() => handleDelete(i.id)} size="sm" variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </>
                  )}
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transfers */}
      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4 flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#D4AF37]" /> Van Transfer Requests</h3>
        <div className="grid gap-3">
          {transfers.filter(t => t.status === 'pending').length === 0 ? (
            <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No pending transfers</div>
          ) : transfers.filter(t => t.status === 'pending').map((t) => (
            <div key={t.id} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-white font-medium">{t.item_name} — {t.quantity} {t.unit}</p>
                <p className="text-[#A3A3A3] text-sm">{t.from_location} → {t.to_location} • Requested by {t.user_name}</p>
                {t.notes && <p className="text-[#525252] text-xs mt-1 italic">{t.notes}</p>}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`transfer-approve-${t.id}`} onClick={() => handleTransferAction(t.id, 'completed')} size="sm" className="bg-green-600 hover:bg-green-700 text-white">Complete</Button>
                <Button onClick={() => handleTransferAction(t.id, 'rejected')} size="sm" variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">Reject</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">Pending Material Requests</h3>
        <div className="grid gap-3">
          {requests.filter(r => r.status === 'pending').length === 0 ? (
            <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No pending requests</div>
          ) : requests.filter(r => r.status === 'pending').map((r) => (
            <div key={r.id || r._id} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-white font-medium">{r.item_name} — {r.quantity_requested} {r.unit}</p>
                  <p className="text-[#A3A3A3] text-sm">Truck: {r.truck_id} • Requested by: {r.user_name}</p>
                </div>
              </div>
              {(r._id || r.id) && (
                <div className="flex gap-2">
                  <Button onClick={() => handleRequestAction(r._id || r.id, 'fulfilled')} size="sm" className="bg-green-600 hover:bg-green-700 text-white">Fulfill</Button>
                  <Button onClick={() => handleRequestAction(r._id || r.id, 'rejected')} size="sm" variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white">Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* History Modal-ish view */}
      {showHistory && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Outfit'] text-xl font-bold text-white flex items-center gap-2"><History className="w-5 h-5 text-[#D4AF37]" /> Inventory History (Last 200 events)</h3>
            <Button onClick={() => setShowHistory(false)} variant="outline" size="sm" className="border-[#262626] text-white">Close</Button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-[#A3A3A3] text-center py-6">No history yet</p>
            ) : history.map((h) => (
              <div key={h.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#D4AF37] text-xs uppercase tracking-wider">{h.type}</span>
                  <span className="text-[#525252] text-xs">{new Date(h.created_at).toLocaleString()}</span>
                </div>
                <p className="text-white mt-1">
                  {h.item_name} {h.delta !== undefined && <span className={h.delta > 0 ? 'text-green-400' : 'text-red-400'}>({h.delta > 0 ? '+' : ''}{h.delta})</span>}
                  {h.from_location && ` • ${h.from_location} → ${h.to_location}`}
                  {h.quantity && ` • ${h.quantity} ${h.unit || ''}`}
                </p>
                <p className="text-[#A3A3A3] text-xs">By: {h.user_name}{h.approved_by_name ? ` • Approved: ${h.approved_by_name}` : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInventory;
